// AudioWavePlayer.tsx
// Self-contained audio player component for Cassette
// Props:
//   - trackId: string (required)
//   - fileId: string (required)
//   - fileName: string (required)
//   - onPlayRequest?: () => void (optional, called when play is requested)
// Exposes play(), pause(), stop() via ref

import { useEffect, useRef, useState, forwardRef, useImperativeHandle, useCallback } from 'react';
import WaveSurfer from 'wavesurfer.js';
import { resolveTrackMediaUrl } from '../services/mediaAccess';
import { getAuth } from 'firebase/auth';
import { useAudioComments } from '../hooks/useAudioComments';
import AudioCommentMarker from './AudioCommentMarker';
import AudioCommentTile from './AudioCommentTile';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { formatTime } from '../utils/timeUtils';

export interface AudioWavePlayerHandle {
  play: () => void;
  pause: () => void;
  stop: () => void;
  getAudioElement: () => HTMLAudioElement | null;
  seek: (time: number) => void;
}

interface AudioWavePlayerProps {
  trackId: string;
  fileId: string;
  fileName: string;
  blobUrl?: string;
  isPaused?: boolean;
  onPause?: () => void;
  onContinue?: () => void;
  onStop?: () => void;
  loadingText?: string;
  isLoading?: boolean;
  canComment?: boolean;
  canView?: boolean;
  onCommentCountChange?: (fileId: string, count: number) => void;
  onCommentAdded?: () => void;
  onCommentClick?: (threadId: string) => void;
  showCommentTiles?: boolean;
  onSeek?: (timestampMs: number) => void;
  comments?: any[]; // External comments to use instead of internal hook
  storageProvider?: string;
  onReady?: () => void;
  onLoadError?: () => void;
}

const AudioWavePlayer = forwardRef<AudioWavePlayerHandle, AudioWavePlayerProps>(
  ({ trackId, fileId, fileName, blobUrl: externalBlobUrl, isPaused, onPause, onStop, loadingText,
    isLoading, canComment = false, canView = false, onCommentCountChange, onCommentAdded, onCommentClick, showCommentTiles = true, onSeek, comments: externalComments, storageProvider, onReady, onLoadError }, ref) => {
    // Note: onContinue is accepted via props but not used - playback control is handled entirely
    // by the parent through isPaused prop. Parent already knows when playback continues.

    const { t } = useTranslation();
    const [blobUrl, setBlobUrl] = useState<string>('');
    const blobRevokeRef = useRef<(() => void) | undefined>(undefined);
    const [audioDuration, setAudioDuration] = useState(0);
    const [audioCurrent, setAudioCurrent] = useState(0);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const waveformRef = useRef<HTMLDivElement | null>(null);
    const waveSurfer = useRef<WaveSurfer | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const isUserSeekingRef = useRef(false);
    const suppressPauseEventRef = useRef(false);
    
    // Comment state
    const [activeTooltipComment, setActiveTooltipComment] = useState<string | null>(null);
    const [showAddCommentForm, setShowAddCommentForm] = useState(false);
    const [addCommentTimestamp, setAddCommentTimestamp] = useState<number>(0);
    
    // Refs for callbacks to avoid stale closures in WaveSurfer event handlers
    const canCommentRef = useRef(canComment);
    const showCommentTilesRef = useRef(showCommentTiles);
    const onSeekRef = useRef(onSeek);
    
    // Keep refs up to date
    useEffect(() => {
      canCommentRef.current = canComment;
      showCommentTilesRef.current = showCommentTiles;
      onSeekRef.current = onSeek;
    }, [canComment, showCommentTiles, onSeek]);
    
    // Use external comments if provided, otherwise use internal hook
    const internalHook = useAudioComments({
      trackId,
      fileId,
      canComment,
      canView,
    });

    const {
      comments,
      userMap,
      createComment,
      resolveComment,
      deleteComment,
    } = externalComments ? {
      comments: externalComments,
      userMap: {},
      createComment: async () => {},
      resolveComment: async () => {},
      deleteComment: async () => {},
    } : internalHook;

    const onReadyRef = useRef(onReady);
    const onLoadErrorRef = useRef(onLoadError);
    useEffect(() => {
      onReadyRef.current = onReady;
      onLoadErrorRef.current = onLoadError;
    }, [onReady, onLoadError]);

    // Use external blobUrl if provided, otherwise fetch
    useEffect(() => {
      if (externalBlobUrl !== undefined) {
        setBlobUrl(externalBlobUrl || '');
        setError('');
        if (!externalBlobUrl) {
          setLoading(false);
        }
        return;
      }
      let cancelled = false;
      setBlobUrl('');
      setAudioCurrent(0);
      setAudioDuration(0);
      setLoading(true);
      setError('');
      if (!fileId || !trackId) return;
      (async () => {
        try {
          const access = await resolveTrackMediaUrl(trackId, fileId, 'audio', storageProvider);
          if (cancelled) {
            access.revoke?.();
            return;
          }
          blobRevokeRef.current?.();
          blobRevokeRef.current = access.revoke;
          setBlobUrl(access.url);
        } catch (e) {
          setError('Failed to load audio.');
          setLoading(false);
          setBlobUrl('');
          console.error('AudioWavePlayer error:', e);
        }
      })();
      return () => {
        cancelled = true;
      };
    }, [trackId, fileId, externalBlobUrl, storageProvider]);

    // Cleanup blob URLs when blobUrl changes or component unmounts
    useEffect(() => {
      return () => {
        blobRevokeRef.current?.();
        blobRevokeRef.current = undefined;
      };
    }, []);

    // Handle waveform seek - called after WaveSurfer seeks
    // WaveSurfer already updated the audio position, we just handle comments
    const handleWaveformSeek = useCallback((position: number, duration: number) => {
      if (duration <= 0) return;
      
      isUserSeekingRef.current = true;
      const seekTimeSeconds = position * duration;
      const seekTimeMs = Math.floor(seekTimeSeconds * 1000);
      
      setAudioCurrent(seekTimeSeconds);
      
      // Handle comment-related behavior based on context
      // This handler is only called from 'interaction' event (user click/drag only)
      if (canCommentRef.current && showCommentTilesRef.current) {
        setAddCommentTimestamp(seekTimeMs);
        setShowAddCommentForm(true);
        setActiveTooltipComment(null);
      }
      
      // Notify parent about seek with timestamp (for AudioCommentsPage)
      if (onSeekRef.current) {
        onSeekRef.current(seekTimeMs);
      }
      
      // Reset seeking flag after a short delay
      setTimeout(() => {
        isUserSeekingRef.current = false;
      }, 100);
    }, []);

    // Setup audio element source when blobUrl changes
    useEffect(() => {
      if (audioRef.current && blobUrl) {
        suppressPauseEventRef.current = true;
        audioRef.current.pause();
        audioRef.current.src = blobUrl;
        audioRef.current.load();
        audioRef.current.currentTime = 0;
        setAudioCurrent(0);
        setShowAddCommentForm(false);
        window.setTimeout(() => {
          suppressPauseEventRef.current = false;
        }, 0);
      }
    }, [blobUrl]);

    // Setup waveform when blobUrl changes (separate from playback element)
    useEffect(() => {
      if (!blobUrl || !waveformRef.current) return;

      setLoading(true);
      setError('');
      setAudioDuration(0);

      if (waveSurfer.current) {
        waveSurfer.current.destroy();
        waveSurfer.current = null;
      }

      const isDarkMode = document.documentElement.classList.contains('dark');
      const waveColor = isDarkMode ? '#6b7280' : '#d1d5db';
      const progressColor = isDarkMode ? '#84cc16' : '#65a30d';

      const ws = WaveSurfer.create({
        container: waveformRef.current,
        waveColor,
        progressColor,
        height: 64,
        barWidth: 2,
        cursorWidth: 1,
        interact: true,
        dragToSeek: true,
      });

      ws.on('ready', () => {
        setAudioDuration(ws.getDuration());
        setLoading(false);
        onReadyRef.current?.();
      });

      ws.on('error', (err) => {
        console.error('AudioWavePlayer WaveSurfer error:', err);
        setError('Failed to load audio.');
        setLoading(false);
        onLoadErrorRef.current?.();
      });

      ws.on('interaction', (newTime: number) => {
        isUserSeekingRef.current = true;
        const duration = ws.getDuration();
        const audio = audioRef.current;

        if (audio && duration > 0) {
          const wasPlaying = !audio.paused;
          audio.currentTime = newTime;
          setAudioCurrent(newTime);

          if (wasPlaying) {
            window.setTimeout(() => {
              if (audio.paused) {
                audio.play().catch((error) => {
                  console.debug('Playback resume failed:', error);
                });
              }
            }, 50);
          }

          handleWaveformSeek(newTime / duration, duration);
        }

        window.setTimeout(() => {
          isUserSeekingRef.current = false;
        }, 100);
      });

      void ws.load(blobUrl);
      waveSurfer.current = ws;

      return () => {
        ws.destroy();
      };
    }, [blobUrl, handleWaveformSeek]);

    // Sync waveform with audio element time updates
    useEffect(() => {
      const audio = audioRef.current;
      if (!audio) return;
      
      const onTimeUpdate = () => {
        // Only update if user is not actively seeking
        if (!isUserSeekingRef.current) {
          setAudioCurrent(audio.currentTime);
          if (waveSurfer.current && waveSurfer.current.getDuration() > 0) {
            waveSurfer.current.seekTo(audio.currentTime / waveSurfer.current.getDuration());
          }
        }
      };
      
      const onPauseEvt = () => {
        if (suppressPauseEventRef.current || !onPause) return;
        onPause();
      };
      const onEndedEvt = () => { setAudioCurrent(0); if (onStop) onStop(); };
      
      audio.addEventListener('timeupdate', onTimeUpdate);
      audio.addEventListener('pause', onPauseEvt);
      audio.addEventListener('ended', onEndedEvt);
      
      return () => {
        audio.removeEventListener('timeupdate', onTimeUpdate);
        audio.removeEventListener('pause', onPauseEvt);
        audio.removeEventListener('ended', onEndedEvt);
      };
    }, [blobUrl, onPause, onStop]);

    // Control play/pause from parent — wait until waveform is ready
    useEffect(() => {
      const audio = audioRef.current;
      if (!audio || !blobUrl || loading) return;

      if (isPaused) {
        suppressPauseEventRef.current = true;
        audio.pause();
        window.setTimeout(() => {
          suppressPauseEventRef.current = false;
        }, 0);
      } else {
        audio.play().catch((err) => {
          console.debug('Audio playback failed:', err);
        });
      }
    }, [isPaused, blobUrl, loading]);

    // Comment marker click handler
    const handleMarkerClick = (threadId: string) => {
      if (onCommentClick) {
        // If callback provided (e.g., on comments page), use it
        onCommentClick(threadId);
      } else {
        // Default behavior (e.g., on track page): toggle tile
        setActiveTooltipComment(activeTooltipComment === threadId ? null : threadId);
        setShowAddCommentForm(false);
      }
    };

    const handleTooltipClose = () => {
      setActiveTooltipComment(null);
    };

    const handleAddComment = async (e: React.MouseEvent | React.KeyboardEvent) => {
      const textarea = (e.target as HTMLElement).closest('.rounded-lg')?.querySelector('textarea') as HTMLTextAreaElement;
      const text = textarea?.value?.trim();
      
      if (!text) return;
      
      try {
        await createComment(addCommentTimestamp, text);
        setShowAddCommentForm(false);
        // Note: toast is shown by createComment hook
        
        // Update comment count after successful creation
        // The createComment hook updates comments state, but React state updates are async
        // So we calculate the new count as current length + 1 (since we just added one)
        if (onCommentCountChange) {
          const newCount = comments.length + 1;
          onCommentCountChange(fileId, newCount);
        }
        
        if (onCommentAdded) {
          onCommentAdded();
        }
      } catch (error) {
        // Error toast is shown by createComment hook
      }
    };

    const handleDeleteComment = async (threadId: string) => {
      if (!threadId) {
        console.error('No threadId provided for delete');
        return;
      }
      const commentToDelete = comments.find(c => c.threadId === threadId);
      const wasUnresolved = commentToDelete && !commentToDelete.resolved;
      
      await deleteComment(threadId);
      setActiveTooltipComment(null);
      
      if (onCommentCountChange) {
        // Calculate new count after deletion
        const newCount = comments.length - 1;
        onCommentCountChange(fileId, newCount);
      }
    };

    const handleResolveComment = async (threadId: string, resolved: boolean) => {
      await resolveComment(threadId, resolved);
    };

    const getCommentPosition = (timestampMs: number) => {
      if (!audioDuration) return 0;
      return (timestampMs / 1000 / audioDuration) * 100;
    };

    useImperativeHandle(ref, () => ({
      play: () => { audioRef.current?.play(); },
      pause: () => { audioRef.current?.pause(); },
      stop: () => {
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
          if (onStop) onStop();
        }
      },
      getAudioElement: () => audioRef.current,
      seek: (time: number) => {
        if (audioRef.current) {
          audioRef.current.currentTime = time;
        }
      },
    }), [onStop]);

    return (
      <div className="w-full flex flex-col items-center mt-2 mb-6">
        {(isLoading || loading) && (
          <div className="flex items-center gap-2 text-xs mb-2" style={{ color: 'var(--brand-from)' }}>
            <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
            {loadingText || 'Loading audio...'}
          </div>
        )}

        {error && <div className="text-xs text-red-600 mb-2">{error}</div>}
        <audio ref={audioRef} preload="auto" />
        <div className="text-xs font-semibold mb-1" style={{ color: 'var(--color-text)' }}>{fileName}</div>
        
        {/* Waveform with comment markers */}
        <div className="w-full relative">
          <div ref={waveformRef} className="w-full" data-testid="audio-waveform" />
          
          {/* Comment markers overlay on waveform */}
          {canView && comments.map((comment) => (
            <div 
              key={comment.threadId}
              className="absolute top-0 left-0 w-full h-full pointer-events-none"
              style={{ height: '64px' }}
            >
              <AudioCommentMarker
                comment={comment}
                position={getCommentPosition(comment.timestampMs)}
                isActive={activeTooltipComment === comment.threadId}
                onClick={() => handleMarkerClick(comment.threadId)}
                canComment={canComment}
                currentUserId={getAuth().currentUser?.uid}
                currentAudioTime={audioCurrent}
                audioDuration={audioDuration}
              />
            </div>
          ))}
        </div>
        
        {/* Time display */}
        <div className="flex items-center justify-between w-full mt-1">
          <span className="text-xs text-gray-500 min-w-[40px]">{formatTime(audioCurrent)}</span>
          <span className="text-xs text-gray-500 min-w-[40px] text-right">{formatTime(audioDuration)}</span>
        </div>

        {/* Comment count display */}
        {canView && comments.length > 0 && (
          <div className="text-xs text-center mt-2" style={{ color: 'var(--color-muted)' }}>
            {comments.length} {comments.length === 1 ? t('audioComments.comment') : t('audioComments.comments')}
          </div>
        )}

        {/* Add comment form - only on track page with showCommentTiles */}
        {canComment && showAddCommentForm && showCommentTiles && (
          <div className="mt-4 flex flex-col lg:flex-row lg:gap-4">
            <div className="flex-1 hidden lg:block" />
            <div className="w-full lg:w-80 lg:max-w-sm">
              <div className="rounded-lg border p-4" style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-semibold" style={{ color: 'var(--brand-from)' }}>
                    @{formatTime(addCommentTimestamp / 1000)}
                  </span>
                  <button
                    onClick={() => setShowAddCommentForm(false)}
                    className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-100"
                    style={{ color: 'var(--color-muted)' }}
                  >
                    ×
                  </button>
                </div>
                <textarea
                  placeholder={t('audioComments.commentPlaceholder')}
                  className="w-full p-2 text-sm rounded border resize-none"
                  style={{ 
                    backgroundColor: 'var(--color-bg)',
                    borderColor: 'var(--color-border)',
                    color: 'var(--color-text)'
                  }}
                  rows={3}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && e.metaKey) {
                      handleAddComment(e);
                    } else if (e.key === 'Escape') {
                      setShowAddCommentForm(false);
                    }
                  }}
                />
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={handleAddComment}
                    className="px-3 py-1 text-xs font-medium rounded transition-colors"
                    style={{ 
                      backgroundColor: 'var(--brand-from)', 
                      color: 'white' 
                    }}
                  >
                    {t('audioComments.addComment')}
                  </button>
                  <button
                    onClick={() => setShowAddCommentForm(false)}
                    className="px-3 py-1 text-xs font-medium rounded transition-colors"
                    style={{ 
                      backgroundColor: 'var(--color-hover)', 
                      color: 'var(--color-text)' 
                    }}
                  >
                    {t('buttons.cancel')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Active comment tile - responsive positioning (only show on track page, not comments page) */}
        {showCommentTiles && canView && activeTooltipComment && (
          <div className="mt-4 flex flex-col lg:flex-row lg:gap-4">
            {/* Spacer for desktop - pushes comment to the right */}
            <div className="flex-1 hidden lg:block" />
            
            {/* Comment tile - full width on mobile, constrained on desktop */}
            <div className="w-full lg:w-80 lg:max-w-sm">
              {(() => {
                const comment = comments.find(c => c.threadId === activeTooltipComment);
                return comment ? (
                  <AudioCommentTile
                    comment={comment}
                    trackId={trackId}
                    fileId={fileId}
                    onDelete={handleDeleteComment}
                    onResolve={handleResolveComment}
                    canComment={canComment}
                    currentUserId={getAuth().currentUser?.uid}
                    onClose={handleTooltipClose}
                    userMap={userMap}
                  />
                ) : null;
              })()}
            </div>
          </div>
        )}
      </div>
    );
  }
);


export default AudioWavePlayer;
